/**
 * 八字格局判断引擎
 * 基于月令（月支主气）与日主的十神关系确定基本格局
 * 结合全局命盘进行格局高低评判
 * 严格遵循子平命理学理论，保证各维度结果一致
 */

import {
  type Stem, type Branch, type Element, type FourPillars,
  STEM_ELEMENT, BRANCH_HIDDEN,
} from '../../baziCalc';
import { getShishen, ELEMENT_COLORS, type Shishen } from './shishenEngine';
import { calcDayStrength } from './xiyongEngine';
import {
  ELEMENT_GENERATES,
  ELEMENT_GENERATED_BY,
  ELEMENT_OVERCOMES,
  ELEMENT_OVERCOME_BY,
  WANGXIANG_TABLE,
} from '@/utils/calcTools/unifiedRules';

// ── 格局类型 ────────────────────────────────────────────────
export type GejuName =
  | '正官格' | '七杀格' | '正印格' | '偏印格'
  | '食神格' | '伤官格' | '正财格' | '偏财格'
  | '比劫格' | '建禄格' | '羊刃格' | '杂气正印格' | '杂气偏印格'
  | '从强格' | '从弱格' | '从财格'
  | '从杀格' | '从儿格' | '化气格';

export type GejuLevel = '上格' | '中上格' | '中格' | '中下格';

export interface GejuResult {
  // ── 原有字段（保持不变，确保向后兼容）─────────────────
  name: GejuName;
  level: GejuLevel;
  mainShishen: Shishen;
  desc: string;
  traits: string[];
  lucky: Element[];
  unlucky: Element[];
  career: string;
  love: string;
  resolve?: string;

  // ── 新增：动态修正层字段（v2）─────────────────────────
  /** 日主天干 */
  dayStem: Stem;
  /** 日主五行 */
  dayStemEl: Element;
  /** 日主强弱 */
  bodyStrength: 'strong' | 'weak' | 'neutral';
  /** 格局成色系数 0-100（基于四柱实际配置的动态评分） */
  gejuPurity: number;
  /** 配置修正标签（如「月令主气透出」「印星护官」「食伤混局」） */
  configAdjustments: string[];
  /** 日主强弱联动文案（同格局不同身强身弱的不同描述） */
  strengthDesc: string;
  /** 格局成色评语（基于 gejuPurity 的动态评级文案） */
  purityComment: string;
  /** 月令主气是否透出天干 */
  monthMainTransparent: boolean;
  /** 月令旺相强度 0-4 */
  monthStrength: number;
  /** 是否清纯无混杂 */
  isPure: boolean;
  /** 是否有印星护格 */
  hasYinProtect: boolean;
  /** 是否有财星生格 */
  hasCaiSupport: boolean;
  /** 是否有食伤破格 */
  hasShiShangBreak: boolean;
  /** 地支是否有日主之根 */
  hasDayRoot: boolean;

  // ── v3：成立度体系 ────────────────────────────────────
  /** 格局成立度 0-100（从格用严格多维评分，正格用成色评分） */
  establishment: number;
  /** 格局倾向文本，如"更接近正官格思路，成立度 72%" */
  tendencyText: string;
  /** 是否为从格 */
  isCongGeju: boolean;
  /** 从格成立度校验摘要（仅从格有值，描述各校验维度） */
  congDiagnosis?: string;
  /** 未达到从格阈值时，保留的从格倾向说明 */
  congTendencyNote?: string;
}

// ── 五行相生相克关系 ────────────────────────────────────────────
// 已统一使用 unifiedRules 中的定义

// ── 动态推导格局喜忌（根据日主五行 + 格局类型 + 日主强弱）─────────
function deriveGejuLuckyUnlucky(
  gejuName: GejuName,
  dayStemEl: Element,
  fourPillars: FourPillars,
): { lucky: Element[]; unlucky: Element[] } {
  const generatedBy = ELEMENT_GENERATED_BY[dayStemEl];
  const generates = ELEMENT_GENERATES[dayStemEl];
  const overcomes = ELEMENT_OVERCOMES[dayStemEl];
  const overcomeBy = ELEMENT_OVERCOME_BY[dayStemEl];

  const bodyStrength = calcDayStrength(fourPillars);

  const lucky: Element[] = [];
  const unlucky: Element[] = [];

  // 从格：顺应强势一方
  if (gejuName.startsWith('从')) {
    switch (gejuName) {
      case '从强格':
        lucky.push(dayStemEl, generatedBy);
        unlucky.push(overcomeBy, generates, overcomes);
        break;
      case '从财格':
        lucky.push(overcomes, generates);
        unlucky.push(dayStemEl, generatedBy, overcomeBy);
        break;
      case '从杀格':
        lucky.push(overcomeBy, overcomes);
        unlucky.push(dayStemEl, generatedBy, generates);
        break;
      case '从儿格':
        lucky.push(generates, overcomes);
        unlucky.push(dayStemEl, generatedBy, overcomeBy);
        break;
      case '从弱格':
      case '化气格':
      default:
        lucky.push(overcomeBy, generates, overcomes);
        unlucky.push(dayStemEl, generatedBy);
        break;
    }
    return { lucky: [...new Set(lucky)], unlucky: [...new Set(unlucky)].filter(e => !lucky.includes(e)) };
  }

  // 普通格局：结合日主强弱推导
  if (bodyStrength === 'strong') {
    lucky.push(overcomeBy, generates, overcomes);
    unlucky.push(dayStemEl, generatedBy);
  } else if (bodyStrength === 'weak') {
    lucky.push(generatedBy, dayStemEl);
    unlucky.push(overcomeBy, generates, overcomes);
  } else {
    lucky.push(generates, overcomes);
    unlucky.push(dayStemEl);
  }

  // 格局特化修正
  if (gejuName === '正官格') {
    if (bodyStrength === 'strong') {
      if (!lucky.includes(overcomes)) lucky.push(overcomes);
      if (!lucky.includes(generatedBy)) lucky.push(generatedBy);
    } else {
      if (!lucky.includes(generatedBy)) lucky.push(generatedBy);
      if (!lucky.includes(dayStemEl)) lucky.push(dayStemEl);
    }
    if (!unlucky.includes(generates)) unlucky.push(generates);
  } else if (gejuName === '七杀格') {
    if (bodyStrength === 'strong') {
      if (!lucky.includes(generates)) lucky.push(generates);
      if (!lucky.includes(overcomes)) lucky.push(overcomes);
    } else {
      if (!lucky.includes(generatedBy)) lucky.push(generatedBy);
      if (!lucky.includes(dayStemEl)) lucky.push(dayStemEl);
    }
  } else if (gejuName === '正印格' || gejuName === '偏印格') {
    if (bodyStrength === 'strong') {
      if (!lucky.includes(overcomes)) lucky.push(overcomes);
      if (!lucky.includes(generates)) lucky.push(generates);
    }
  } else if (gejuName === '食神格' || gejuName === '伤官格') {
    if (bodyStrength === 'weak') {
      if (!lucky.includes(generatedBy)) lucky.push(generatedBy);
      if (!lucky.includes(dayStemEl)) lucky.push(dayStemEl);
    } else {
      if (!lucky.includes(overcomes)) lucky.push(overcomes);
    }
  } else if (gejuName === '正财格' || gejuName === '偏财格') {
    if (bodyStrength === 'weak') {
      if (!lucky.includes(generatedBy)) lucky.push(generatedBy);
      if (!lucky.includes(dayStemEl)) lucky.push(dayStemEl);
    } else {
      if (!lucky.includes(generates)) lucky.push(generates);
      if (!lucky.includes(overcomes)) lucky.push(overcomes);
    }
  }

  return {
    lucky: [...new Set(lucky)],
    unlucky: [...new Set(unlucky)].filter(e => !lucky.includes(e)),
  };
}

// ── 格局性格描述库 ─────────────────────────────────────────
interface GejuProfile {
  brief: string;
  traits: string[];
  career: string;
  love: string;
}

const GEJU_PROFILES: Record<GejuName, GejuProfile> = {
  正官格: {
    brief: '正官格主规矩中正，自律守信，处事公平，有管理之才，社会认可度高',
    traits: ['自律守正', '处事公正', '责任感强', '重视名誉', '克己奉公'],
    career: '命局官星清纯为用，事业格局以「循规晋升·权威渐积」为底色。官星透干则名声显于世，月令得官则仕途有根。身强宜担官职，宜精不宜滥；官杀并见需取一制一方能清纯有用，最忌食伤混入破格。遇官杀混杂时，不必焦虑，以印星化杀为上策，逐步梳理格局方向即可。',
    love: '正官为夫（妻）星正位，感情命理取向稳健传统。官星清纯者婚姻有序，宜门当户对、有社会认可的配偶。官杀混杂者情路见波折，需印星化杀方能稳定。女命正官透干婚缘早，男命以正财为妻星、正官主社会责任，宜先立业后成家。',
  },
  七杀格: {
    brief: '七杀格主魄力过人，勇于挑战，在逆境中激发潜能，成就卓越事业——七杀有制则为权',
    traits: ['魄力十足', '勇于挑战', '意志坚定', '领导力强', '不畏压力'],
    career: '命局七杀为用，事业格局以「压力驱动·逆境突破」为底色。七杀有制（食伤制杀或印星调和）则调和为用，威名远播；七杀无制则激进招祸，格局高低全在制化手段。这不是坏命，而是提醒您这一阶段更适合建立边界、降低冒进、用成果说话。身强杀旺为上格，身弱逢杀需印星生身方可驾驭。',
    love: '七杀为克星，感情命理带有强烈的磁场冲突色彩。七杀有制者感情深沉有力，对伴侣守护意识极强；七杀无制者感情易生争执、关系冲突频发。这提醒您以守护代替管控，在感情中给伴侣更多自主空间。女命七杀为偏夫星，婚缘有异，宜食伤制杀以稳婚姻；男命逢七杀之年感情有波动，需防冲动损缘。',
  },
  正印格: {
    brief: '正印格主仁厚善良，学识广博，贵人运旺，得长辈、师长庇护',
    traits: ['善良仁厚', '学识渊博', '贵人运旺', '宽以待人', '重精神追求'],
    career: '命局印星为用，事业格局以「学识积累·贵人庇护」为底色。印星透干则贵人缘旺，月令得印则学有根基。印旺需财星制印方能流通，无财则印旺成病，学识积累难以变现。宜在文化、知识、公共服务类领域深耕，声誉是最大的无形资产。',
    love: '正印为母星，感情命理取向重精神滋养，婚姻中长辈意见权重高。印旺者感情决策偏慢，易受家庭影响；印弱者感情中自我保护意识强。女命正印旺则婚后相夫教子意愿强，宜选有学识涵养的伴侣；男命印旺则母缘深，择偶时宜防母缘影响婚缘。',
  },
  偏印格: {
    brief: '偏印格思维独特，洞察力强，学识专精，善于钻研，富有哲思',
    traits: ['思维独特', '洞察深刻', '博而专精', '独立内省', '喜研究钻探'],
    career: '命局偏印为用，事业格局以「独辟蹊径·专精深钻」为底色。偏印为偏门之星，常规晋升路线难以发挥其效，非传统领域反而更易成名。偏印忌食伤制化，食伤旺之年才华受阻；逢财星流年偏印被克，宜守不宜攻。',
    love: '偏印为克食伤之星，感情命理上有情感表达受阻之象。偏印旺者感情内敛，不善表达，婚缘偏慢热；偏印逢伤官之年情感反而流动增加。女命偏印重则子星受制，需注意；男命偏印旺则感情中习惯独处，需伴侣有耐心理解。',
  },
  食神格: {
    brief: '食神格主福禄绵延，乐观聪明，才华内蕴，生活富有享受与品位',
    traits: ['聪明乐观', '才华横溢', '生活品位高', '处事从容', '享受人生'],
    career: '命局食神为用，事业格局以「才华流露·福禄自来」为底色。食神制杀为最佳配置，将竞争压力转化为才华产出，事业成就不落言筌。食神生财则财运绵延；食神遇偏印为枭，才华反受克制，需防枭神夺食之格。',
    love: '食神为我生之星，感情命理取向温润悠然。食神旺者感情态度从容，不急不躁，对伴侣宽容包容；女命食神为子星旺地，生育缘深。食神遇偏印被克则感情中才华受压，需防情感表达受阻；食伤生财之配置则感情生活品质俱佳。',
  },
  伤官格: {
    brief: '伤官格聪颖出众，创新力强，个性不拘一格，勇于突破常规——这是改变世界的原动力',
    traits: ['才华卓绝', '创新突破', '表达力强', '个性鲜明', '思想超前'],
    career: '命局伤官为用，事业格局以「突破常规·才华制胜」为底色。伤官见财为吉，才华变现能力强；伤官见官容易有是非，需印星调候化解，这不是坏事，而是提醒您少说多做、用成果表达。伤官格最佳大运是食伤生财或印星流年，印星年份是伤官格进攻事业的黄金窗口。',
    love: '伤官克官，感情命理上感情格局较为复杂。女命伤官重则官星受损，正缘难稳，宜食伤有制方能感情安定；男命伤官旺则口舌惹祸，感情中言语易伤人。伤官格感情宜「印年论婚」——印星流年是谈婚论嫁的最佳时机，伤官当令之年感情多波折，这不是不幸，而是时机未到的提醒。',
  },
  正财格: {
    brief: '正财格踏实务实，诚信守约，理财有道，财富通过持续努力稳健积累',
    traits: ['务实稳健', '诚信守约', '理财有道', '重视物质基础', '勤劳持家'],
    career: '命局正财为用，事业格局以「踏实积累·稳健生财」为底色。身强财旺为富格，财星得月令之气则财源有根。正财忌比劫争财，比劫旺之年财务受损；食伤生财为最佳配置，以才华换稳定收入。正财格事业运最怕命局身弱财旺，财多反成压力，此时先固本再求财即可。',
    love: '正财为男命妻星正位，感情命理取向踏实专一。男命正财透干者婚缘较顺，配偶持家有道；正财被比劫夺则婚姻有争，需防第三者。女命正财旺则理财意识强，婚姻中经济责任感重；身弱逢财则感情中容易为物质所累，宜先强身后论婚。',
  },
  偏财格: {
    brief: '偏财格八面玲珑，人脉广泛，善于把握机会，财运在流动中产生',
    traits: ['八面玲珑', '人际关系强', '善抓机会', '慷慨大方', '财源多元'],
    career: '命局偏财为用，事业格局以「广结人脉·流动生财」为底色。偏财为父星兼流动之财，食伤生偏财则财源广进；比劫旺之年争财破耗，需防合伙纠纷，宜先定规则再合作。偏财格最佳流年是食伤生财叠加，此时可大胆扩张人脉资源。',
    love: '偏财为男命父星兼桃花星，感情命理取向多元活跃。男命偏财旺则桃花缘深，感情选择多但专情难；偏财透干逢比劫则情场竞争者出现。女命偏财旺则财务独立意识强，婚姻中重视经济对等；偏财格宜在食伤生财的大运中缔结稳定感情，此时感情质量最高。',
  },
  比劫格: {
    brief: '比劫格自我意识强，独立自主，竞争力旺，适合在竞争中脱颖而出',
    traits: ['独立自主', '竞争意识强', '勇于开拓', '重情义', '有魄力'],
    career: '命局以比劫为格，事业格局以「自立门户·竞争取胜」为底色。比劫格最需官杀制劫方能约束，无官杀则散漫难成大器；食伤泄秀则将旺气转化为才华产出。比劫过旺最忌财星——财多比旺则争财内耗，宜以官杀定格局方向。',
    love: '比劫为同类，感情命理上婚姻格局以「独立对等」为核心。比劫过旺者感情中主导欲强，易压制伴侣；官杀制劫之年感情有收获，此时婚缘好。男命比劫旺则财星（妻星）受夺，婚姻易有争议；女命比劫旺则官杀（夫星）力弱，婚缘宜晚不宜早。',
  },
  建禄格: {
    brief: '建禄格主日主得月令禄地，根气扎实，自立性强，格局关键在于有官杀约束、食伤泄秀或财星流通',
    traits: ['根气扎实', '自立性强', '执行稳定', '重视掌控', '宜制化流通'],
    career: '命局月令临日主禄地，事业格局以「得令有根·自立成事」为底色。建禄不同于普通比劫格，重点不在同类多寡，而在月令给日主根基。禄旺宜见官杀定规则，或见食伤泄秀、财星流通；若只见比劫而少制化，则容易力足而方向分散。',
    love: '建禄格感情命理上自我根气较足，关系中重稳定与现实承担。若财官配合得宜，责任感强、经营能力稳；若禄旺无制，需留意过度坚持己见，感情中宜多听取对方需求。',
  },
  羊刃格: {
    brief: '羊刃格主日主临旺刃之地，胆识强、行动快，格局关键在于有官杀制刃、食伤泄秀或财星流通',
    traits: ['胆识果断', '竞争力强', '执行迅速', '抗压性高', '需要约束成器'],
    career: '命局月令临羊刃，事业格局以「旺气成刃·以制为贵」为底色。刃旺者不怕竞争，怕的是无制无泄而过刚；见官杀制刃、食伤泄秀、财星流通，则能把锋芒转为担当与成果。此格宜在管理、技术、竞技、创业等需要决断力的领域发挥。',
    love: '羊刃格感情命理上自我力量较强，关系中容易主导。若官杀、财星配合得宜，则责任感强、保护欲足；若刃气无制，需留意沟通中的强势与急躁。感情宜在规则清楚、互相尊重的关系里稳定发展。',
  },
  杂气正印格: {
    brief: '杂气正印格多见辰戌丑未墓库月，印星透出而成格，重学识、资质、贵人和长期积累',
    traits: ['重视学习', '稳中有藏', '贵人缘厚', '耐力较强', '适合长期积累'],
    career: '命局墓库月令藏气复杂，正印透出则以「杂气正印」取格。此格不取月令主气一刀切，而看库中余气与天干透出是否成用。事业宜走知识、资质、管理、文化、教育、专业服务等路线，重在持续累积。',
    love: '杂气正印格感情取向较稳，重安全感与精神支持。印星为用时，长辈、家庭、学历或资质背景常会影响关系选择；过旺则需防想得多、行动慢，宜让关系在现实互动中落地。',
  },
  杂气偏印格: {
    brief: '杂气偏印格多见辰戌丑未墓库月，偏印透出而成格，思维独特，适合专精研究与非标准路径',
    traits: ['洞察独特', '专精钻研', '内省敏锐', '善走偏门', '需要稳定输出'],
    career: '命局墓库月令藏气复杂，偏印透出则以「杂气偏印」取格。此格重视隐含资源与专业壁垒，适合研究、技术、咨询、玄学文化、设计策划等需要深度思考的领域。若能配合食伤输出，专长更易变现。',
    love: '杂气偏印格感情表达偏内敛，容易先观察、后投入。关系中需要理解空间与精神共鸣；若偏印过重，则需主动表达真实需求，避免让伴侣猜测。',
  },
  从强格: {
    brief: '从强格日主极旺，性格坚定独立，适合顺势而为，以旺为用',
    traits: ['性格坚定', '自我主见强', '行动力强', '独立性高', '不甘居下'],
    career: '从强格日主极旺，以旺为用，顺日主五行特性精深发展。命局最忌财官克身破格，见财官之年运势急转直下，宜提前识别破格信号、收缩防守。顺势而为、强化优势是从强格事业最高原则，不宜逆势扩张到弱势领域。',
    love: '从强格感情命理上以独立主导为取向，伴侣角色更近于协作者而非依附者。破格之年（财官流入）感情亦随格局动荡，宜在纯格之年缔结稳定感情。伴侣需有足够的包容力与独立性，过度依赖者易与从强格产生冲突。',
  },
  从弱格: {
    brief: '从弱格日主极衰，以弱为用，顺势接受帮助，贵人之缘厚',
    traits: ['随和谦逊', '善于借力', '适应性强', '贵人缘旺', '柔中带韧'],
    career: '从弱格日主极衰，以官杀印星为用，善借体制与他人之力。命局最忌比劫帮身破格，比劫流年格局坍塌、运势下跌。贵人星旺的大运是从弱格最佳进取窗口，此时借势可成就超越自身本格的事业高度。',
    love: '从弱格感情命理以「依托有力伴侣」为底色，宜选择身强有担当的配偶。破格之年（比劫大量涌入）感情亦受扰动，宜稳不宜变。官杀星旺的流年缔结婚姻，婚姻命理最稳；比劫旺年感情竞争者增多，需防横刀夺爱。',
  },
  从财格: {
    brief: '从财格财星极旺，善于经营，具有很强的财务嗅觉与商业眼光',
    traits: ['财务嗅觉灵敏', '善于经营', '实用主义', '开放进取', '事业心强'],
    career: '从财格财星极旺，以财为专一用神，命局顺财势则兴、逆财势则败。最忌比劫争财破格，比劫流年宜收缩战线、让利保全格局。食伤生财之大运是从财格最佳进攻窗口，此时可大胆扩张商业版图。',
    love: '从财格感情命理以「务实利益」为底色，伴侣的经济能力与资源整合力是重要考量。财旺之年感情机缘多；比劫争财之年感情亦有竞争者。男命从财格妻星（财星）极旺，婚缘较多但需防因财生争；女命从财格独立性强，婚姻中经济地位对等是长久之道。',
  },
  从杀格: {
    brief: '从杀格七杀极旺，在压力与挑战中磨炼意志，成就不凡',
    traits: ['抗压能力强', '意志如铁', '勇于担当', '处变不惊', '有领袖气质'],
    career: '从杀格七杀极旺，以杀为专一用神，在高压与挑战中反而能激发极致潜能。命局最忌食伤制杀破格，食伤流年宜收敛锋芒、强化执行力。七杀旺的大运是从杀格最佳晋升窗口，此时压力与成就并行。',
    love: '从杀格感情命理带有强烈的「克制磁场」，伴侣需有足够的心理强度与包容力。杀旺之年感情压力大但情感反而深沉；食伤流年破格则感情随格局动荡，宜稳不宜变。男命从杀格婚姻中主导性极强，需学习平等相处；女命从杀格则夫星（官杀）极旺，婚缘早且感情浓烈。',
  },
  从儿格: {
    brief: '从儿格食伤极旺，才华横溢，创造力惊人，思想超前',
    traits: ['创造力超强', '思想超前', '才华出众', '个性自由', '擅于表达'],
    career: '从儿格食伤极旺，以食伤为专一用神，才华变现是最直接的事业路径。食伤生财之配置则财运随才华而来；命局最忌印星克制食伤破格，印星流年才华受阻，宜减少学习、增加输出。',
    love: '从儿格感情命理以「才华共鸣」为底色，伴侣需能欣赏并激发其创意灵感。印星破格之年感情中才华表达受限，反而不利于感情；食伤生财旺年感情亦活跃，才华魅力在此时最能吸引优质伴侣。女命食伤为子星旺地，生育缘极深。',
  },
  化气格: {
    brief: '化气格日主与干合化，格局特殊，性格中有化气五行的特质',
    traits: ['处事灵活', '随机应变', '内外协调', '善于融合', '个性独特'],
    career: '化气格日主与合化五行专一为用，事业命理底色由化气五行属性决定。化气格以合化纯粹为贵，逢冲则化气受损，破格之年宜守不宜攻。事业发展宜沿化气五行对应的行业深耕，跨界融合反而能出奇制胜。',
    love: '化气格感情命理以「圆融调和」为底色，在感情中善于消化冲突、融合差异。逢冲破格之年感情亦随格局动荡，宜稳不宜变；化气纯粹之年感情最和谐。感情中灵活变通是优势，但也需防止因太会调和而失去真实自我。',
  },
};

// ── 日主强弱联动文案库 ────────────────────────────────────
const STRENGTH_GEJU_DESC: Record<GejuName, Record<'strong' | 'weak' | 'neutral', string>> = {
  正官格: {
    strong: '日主健旺，官星得用而不为其所累，适合争取晋升与权力，是事业进阶的关键期。身强担官，权威与责任并重，领导才能得以充分发挥。',
    weak: '日主偏弱，官星压力偏大，宜稳扎稳打，借力印星或贵人扶持方能胜任。身弱见官，需防过度承担导致身心透支，以柔克刚为上策。',
    neutral: '日主中和，官星适中，既能担责又不过度受压。宜循序渐进，在规矩中求发展，稳健是最大优势。',
  },
  七杀格: {
    strong: '日主健旺，七杀有制化之机，逆境中能激发最大潜能。身强抗杀，魄力与行动力并存，适合挑战高难度目标。',
    weak: '日主偏弱，七杀克身之力过重，压力虽大但需防身心透支。身弱逢杀，宜寻求印星化杀生身或比劫抗杀，切勿单打独斗。',
    neutral: '日主中和，七杀之力适中，既有挑战又不至于崩溃。在压力下稳步成长，宜以韧克刚，稳中求进。',
  },
  正印格: {
    strong: '日主健旺，印星生身有力但需防印多为病。身强印旺，学识广博但宜防思想保守，需以财星破印、食伤泄秀以求流通。',
    weak: '日主偏弱，印星是最佳庇护，得长辈、师长、贵人多方扶持。身弱逢印，学业事业皆有靠山，宜主动借力。',
    neutral: '日主中和，印星适中，既有学识涵养又不至于过旺埋金。宜在文化中深耕，以知识为立身之本。',
  },
  偏印格: {
    strong: '日主健旺，偏印思维独特，洞察深刻。身强偏印，专精领域有独到见解，但需防孤僻内向，宜多与人交流碰撞思想。',
    weak: '日主偏弱，偏印虽生身但力量偏斜，思维虽活跃但精力不足。身弱逢偏印，宜选择有人带领的研究环境，借力发展。',
    neutral: '日主中和，偏印之力适中，既有独特思维又有足够精力支撑。宜在专精领域深耕，以独特视角取胜。',
  },
  食神格: {
    strong: '日主健旺，食神泄秀有力，才华得以充分展现。身强食神，福禄双全，创意与生活品位俱佳，适合艺术、设计等展现才华的领域。',
    weak: '日主偏弱，食神泄气使弱身更弱，才华虽有意但精力不继。身弱逢食神，宜精简项目聚焦核心，避免过度消耗。',
    neutral: '日主中和，食神泄秀适中，才华与精力平衡。宜在悠然中成就，不必急于求成，循序渐进即可。',
  },
  伤官格: {
    strong: '日主健旺，伤官创新突破之力得以发挥。身强伤官，才华卓绝，适合在需要突破常规的领域大展身手，但需防恃才傲物。',
    weak: '日主偏弱，伤官泄气加重负担，创新虽有心但力不足。身弱逢伤官，宜先固本培元，待身强时再展才华。',
    neutral: '日主中和，伤官之力适中，既有创新意识又有执行力支撑。宜在专业领域适度突破，以稳为基。',
  },
  正财格: {
    strong: '日主健旺，财星为日主所克，身旺担财能力强劲。身强正财，理财有道，财富积累稳健，适合实业、金融等需要踏实积累的领域。',
    weak: '日主偏弱，财星耗身使弱身更弱，财来财去难以留存。身弱逢财，宜以守为主，量入为出，先固本再求财。',
    neutral: '日主中和，财星适中，既有求财之心又有担财之力。宜稳健理财，不贪不冒，循序渐进积累。',
  },
  偏财格: {
    strong: '日主健旺，偏财八面玲珑，善于抓机会。身强偏财，财源多元，人脉广泛，适合商贸、投资等流动性强的领域。',
    weak: '日主偏弱，偏财耗身，机会虽多但精力不继。身弱逢偏财，宜减少投机，聚焦主业，避免贪多嚼不烂。',
    neutral: '日主中和，偏财之力适中，既有机会意识又有把控能力。宜在稳健中把握机会，不贪大求全。',
  },
  比劫格: {
    strong: '日主健旺，比劫帮身使身更强，自我意识突出。身强比劫，竞争力旺，适合创业、竞技等竞争性领域，但需防刚愎自用。',
    weak: '日主偏弱，比劫是最佳帮扶，同类相助使弱身有力。身弱逢比劫，宜结交盟友，合作共赢，借助团队力量壮大自身。',
    neutral: '日主中和，比劫适中，既有独立意识又有协作精神。宜在竞争中保持合作，在合作中保持独立。',
  },
  建禄格: {
    strong: '日主得月令禄地，根气充足，自我驱动力强。身旺建禄宜见官杀制身、食伤泄秀或财星流通，避免只靠硬扛而缺少方向。',
    weak: '虽为建禄，原局仍需看透干与全局消长。若日主偏弱，月令禄地是重要根基，宜先稳住自身节奏，再借食伤财官把力量转为成果。',
    neutral: '日主得禄而不过偏，根基与输出较易平衡。宜在稳定节奏中建立规则、输出能力与现实成果。',
  },
  羊刃格: {
    strong: '日主临刃而旺，行动力和竞争力突出。羊刃喜官杀制伏、食伤泄秀，能制能泄则锋芒成器。',
    weak: '羊刃格以月令旺刃为核心，此处不以普通弱身论。若全局扶助不足，宜先建立秩序与资源，再发挥决断力。',
    neutral: '日主得刃，力量不弱。宜在规则与目标中释放行动力，避免过刚过急。',
  },
  杂气正印格: {
    strong: '杂气正印透出，日主有印星资助。身强时需避免印重成滞，宜以财星或食伤令格局流通。',
    weak: '杂气正印透出而扶身，弱身得印为助。宜借助资质、贵人、学习体系稳步发展。',
    neutral: '杂气正印力量适中，知识与现实承载较平衡。宜持续积累专业壁垒。',
  },
  杂气偏印格: {
    strong: '杂气偏印透出，思维独特但需防孤高内耗。身强宜增加输出与实践，让专长落地。',
    weak: '杂气偏印扶身，弱身得偏印可借专业、技术、研究之力立身。宜选择有框架的深耕路径。',
    neutral: '杂气偏印适中，洞察与行动可相互配合。宜在专精领域形成个人方法。',
  },
  从强格: {
    strong: '日主极旺，从强之势已成，性格坚定独立。以旺为用，顺势而为，在本行业精深发展，不宜逆势而行。',
    weak: '从强格日主极旺，此格不以弱论。从强之势需全局配合，宜顺应日主五行特性，强化自身优势领域。',
    neutral: '从强格日主极旺，格局特殊。宜顺势而为，以同类扶强，在专业领域持续深耕，不轻言改变方向。',
  },
  从弱格: {
    strong: '从弱格日主极衰，此格不以强论。从弱之势需全局无根无印，顺势接受帮助，贵人之缘深厚。',
    weak: '日主极衰，从弱之势已成，性格随和谦逊。善于借力，适应性强，宜选择有组织支持的工作环境，善借他人之力。',
    neutral: '从弱格日主极衰，格局特殊。宜顺势接受帮助，贵人之缘厚，在团队中发挥协调与配合能力。',
  },
  从财格: {
    strong: '从财格财星极旺，此格不以强论。财务嗅觉灵敏，善于经营，适合商业、金融等与财富直接相关的领域。',
    weak: '日主极衰，财星极旺，从财之势已成。实用主义，开放进取，商场如鱼得水，事业心强。',
    neutral: '从财格财星极旺，格局特殊。宜顺应财星之势，在商业、贸易等领域深耕，以财富为导向规划人生。',
  },
  从杀格: {
    strong: '从杀格七杀极旺，此格不以强论。抗压能力强，意志如铁，适合军警、竞技、高压管理等挑战性领域。',
    weak: '日主极衰，七杀极旺，从杀之势已成。逆境中磨炼意志，处变不惊，具有领袖气质，逆境展现惊人潜力。',
    neutral: '从杀格七杀极旺，格局特殊。宜顺应杀势，在压力与挑战中成长，将逆境转化为成就的动力。',
  },
  从儿格: {
    strong: '从儿格食伤极旺，此格不以强论。创造力超强，思想超前，最适合艺术、媒体、创意产业。',
    weak: '日主极衰，食伤极旺，从儿之势已成。才华出众，个性自由，擅于表达，才华是最大的财富。',
    neutral: '从儿格食伤极旺，格局特殊。宜顺应食伤之势，在创意、表达、艺术领域深耕，以才华立身。',
  },
  化气格: {
    strong: '化气格日主与干合化，此格不以常理论。处事灵活，随机应变，依化气五行特性选择职业方向。',
    weak: '化气格日主与干合化，格局特殊。内外协调，善于融合，能在特定领域发挥独到才能。',
    neutral: '化气格日主与干合化，格局特殊。个性独特，善于调和与融合，伴侣关系和谐，宜依化气特性发展。',
  },
};

function getMonthStrength(element: string, monthBranch: Branch): number {
  return WANGXIANG_TABLE[element as Element]?.[monthBranch] ?? 2;
}

const YANG_REN_MONTH_BRANCH: Partial<Record<Stem, Branch>> = {
  甲: '卯',
  丙: '午',
  戊: '午',
  庚: '酉',
  壬: '子',
};

const JIAN_LU_MONTH_BRANCH: Record<Stem, Branch> = {
  甲: '寅',
  乙: '卯',
  丙: '巳',
  丁: '午',
  戊: '巳',
  己: '午',
  庚: '申',
  辛: '酉',
  壬: '亥',
  癸: '子',
};

const STORAGE_BRANCHES = new Set<Branch>(['辰', '戌', '丑', '未']);

function isYangRenMonth(dayStem: Stem, monthBranch: Branch): boolean {
  return YANG_REN_MONTH_BRANCH[dayStem] === monthBranch;
}

function isJianLuMonth(dayStem: Stem, monthBranch: Branch): boolean {
  return JIAN_LU_MONTH_BRANCH[dayStem] === monthBranch;
}

function findMixedQiYinPattern(dayStem: Stem, monthBranch: Branch, allStems: Stem[]): '杂气正印格' | '杂气偏印格' | null {
  if (!STORAGE_BRANCHES.has(monthBranch)) return null;

  const visibleShishen = allStems
    .filter(stem => stem !== dayStem)
    .map(stem => getShishen(dayStem, stem));

  if (visibleShishen.includes('正印')) return '杂气正印格';
  if (visibleShishen.includes('偏印')) return '杂气偏印格';
  return null;
}

// ═══════════════════════════════════════════════════════════════
//  动态修正层 — 四柱配置分析
// ═══════════════════════════════════════════════════════════════

/**
 * 检测地支中是否有日主之根
 */
function checkDayRoot(fourPillars: FourPillars): boolean {
  const dayStemEl = STEM_ELEMENT[fourPillars.day.stem];
  const allBranches = [fourPillars.year.branch, fourPillars.month.branch, fourPillars.day.branch, fourPillars.hour.branch];
  for (const b of allBranches) {
    for (const h of BRANCH_HIDDEN[b]) {
      if (STEM_ELEMENT[h.stem] === dayStemEl) return true;
    }
  }
  return false;
}

/**
 * 检测是否有印星护格
 * 印星 = 生我之五行，出现在天干或地支主气中
 */
function checkYinProtect(fourPillars: FourPillars, gejuName: GejuName): boolean {
  const dayStemEl = STEM_ELEMENT[fourPillars.day.stem];
  const yinEl = ELEMENT_GENERATED_BY[dayStemEl];
  const allStems = [fourPillars.year.stem, fourPillars.month.stem, fourPillars.day.stem, fourPillars.hour.stem];
  // 天干有印星
  for (const s of allStems) {
    if (STEM_ELEMENT[s] === yinEl) return true;
  }
  // 地支主气有印星
  const allBranches = [fourPillars.year.branch, fourPillars.month.branch, fourPillars.day.branch, fourPillars.hour.branch];
  for (const b of allBranches) {
    const mainStem = BRANCH_HIDDEN[b][0]?.stem;
    if (mainStem && STEM_ELEMENT[mainStem] === yinEl) return true;
  }
  return false;
}

/**
 * 检测是否有财星支持格局
 * 财星 = 我克之五行
 */
function checkCaiSupport(fourPillars: FourPillars): boolean {
  const dayStemEl = STEM_ELEMENT[fourPillars.day.stem];
  const caiEl = ELEMENT_OVERCOMES[dayStemEl];
  const allStems = [fourPillars.year.stem, fourPillars.month.stem, fourPillars.day.stem, fourPillars.hour.stem];
  for (const s of allStems) {
    if (STEM_ELEMENT[s] === caiEl) return true;
  }
  return false;
}

/**
 * 检测是否有食伤破格
 * 食伤 = 我生之五行，对官格、杀格而言是破格
 */
function checkShiShangBreak(fourPillars: FourPillars, gejuName: GejuName): boolean {
  if (!['正官格', '七杀格'].includes(gejuName)) return false;
  const dayStemEl = STEM_ELEMENT[fourPillars.day.stem];
  const shishangEl = ELEMENT_GENERATES[dayStemEl];
  const allStems = [fourPillars.year.stem, fourPillars.month.stem, fourPillars.day.stem, fourPillars.hour.stem];
  for (const s of allStems) {
    if (STEM_ELEMENT[s] === shishangEl) return true;
  }
  // 地支主气
  const allBranches = [fourPillars.year.branch, fourPillars.month.branch, fourPillars.day.branch, fourPillars.hour.branch];
  for (const b of allBranches) {
    const mainStem = BRANCH_HIDDEN[b][0]?.stem;
    if (mainStem && STEM_ELEMENT[mainStem] === shishangEl) return true;
  }
  return false;
}

/**
 * 检测格局是否清纯
 * 清纯 = 月令主气所代表的十神在全局中没有被严重混杂
 */
function checkGejuPure(fourPillars: FourPillars, gejuName: GejuName, mainShishen: Shishen): boolean {
  const dayStem = fourPillars.day.stem;
  const allStems = [fourPillars.year.stem, fourPillars.month.stem, fourPillars.day.stem, fourPillars.hour.stem];

  // 官格/杀格：检查是否有食伤破格
  if (['正官格', '七杀格'].includes(gejuName)) {
    const shishangEl = ELEMENT_GENERATES[STEM_ELEMENT[dayStem]];
    for (const s of allStems) {
      if (STEM_ELEMENT[s] === shishangEl) return false;
    }
  }

  // 印格：检查是否有财星破印
  if (['正印格', '偏印格'].includes(gejuName)) {
    const caiEl = ELEMENT_OVERCOMES[STEM_ELEMENT[dayStem]];
    for (const s of allStems) {
      if (STEM_ELEMENT[s] === caiEl) return false;
    }
  }

  // 财格：检查是否有比劫争财
  if (['正财格', '偏财格'].includes(gejuName)) {
    const sameEl = STEM_ELEMENT[dayStem];
    for (const s of allStems) {
      if (s !== dayStem && STEM_ELEMENT[s] === sameEl) return false;
    }
  }

  // 食伤格：检查是否有印星制食伤
  if (['食神格', '伤官格'].includes(gejuName)) {
    const yinEl = ELEMENT_GENERATED_BY[STEM_ELEMENT[dayStem]];
    for (const s of allStems) {
      if (STEM_ELEMENT[s] === yinEl) return false;
    }
  }

  return true;
}

/**
 * 构建配置修正标签
 */
function buildConfigAdjustments(
  fourPillars: FourPillars,
  gejuName: GejuName,
  mainShishen: Shishen,
  monthMainTransparent: boolean,
  monthStrength: number,
  isPure: boolean,
  hasYinProtect: boolean,
  hasCaiSupport: boolean,
  hasShiShangBreak: boolean,
  hasDayRoot: boolean,
  bodyStrength: 'strong' | 'weak' | 'neutral',
): string[] {
  const adjustments: string[] = [];

  // 1. 月令透出
  if (monthMainTransparent) {
    adjustments.push(monthStrength >= 3 ? '月令主气透出 · 格局清纯有力' : '月令主气透出 · 格局成立');
  } else {
    adjustments.push('月令主气未透 · 格局暗含');
  }

  // 2. 月令旺相
  if (monthStrength >= 3) adjustments.push('月令旺相 · 得时得气');
  else if (monthStrength <= 1) adjustments.push('月令休囚 · 格局力弱');

  // 3. 清纯/混杂
  if (isPure) adjustments.push('格局清纯 · 无混杂破格');
  else adjustments.push('格局有混杂 · 需调和');

  // 4. 护格/破格
  if (hasYinProtect) adjustments.push('印星护格 · 化杀生身');
  if (hasCaiSupport) adjustments.push('财星生格 · 财源有根');
  if (hasShiShangBreak) adjustments.push('食伤破格 · 需印化解');

  // 5. 日主根气
  if (hasDayRoot) adjustments.push(bodyStrength === 'strong' ? '日主有根 · 身强有力' : '日主有根 · 弱中有靠');
  else adjustments.push('日主无根 · 从格嫌疑');

  // 6. 身强身弱对格局的影响
  if (bodyStrength === 'strong') {
    if (['正官格', '七杀格'].includes(gejuName)) adjustments.push('身强可担 · 官杀为用');
    else if (['正财格', '偏财格'].includes(gejuName)) adjustments.push('身强可担 · 财星流通');
    else if (['食神格', '伤官格'].includes(gejuName)) adjustments.push('身强泄秀 · 才华可展');
  } else if (bodyStrength === 'weak') {
    if (['正印格', '偏印格'].includes(gejuName)) adjustments.push('身弱逢印 · 庇护有力');
    else if (['正官格', '七杀格'].includes(gejuName)) adjustments.push('身弱见官 · 压力偏大');
    else if (['正财格', '偏财格'].includes(gejuName)) adjustments.push('身弱逢财 · 宜守不宜攻');
  }

  return adjustments;
}

/**
 * 计算格局成色系数 0-100
 */
function calcGejuPurity(
  monthMainTransparent: boolean,
  monthStrength: number,
  isPure: boolean,
  hasYinProtect: boolean,
  hasCaiSupport: boolean,
  hasDayRoot: boolean,
  bodyStrength: 'strong' | 'weak' | 'neutral',
  gejuName: GejuName,
): number {
  let score = 40; // 基础分

  // 月令透出 +20（上格核心条件）
  if (monthMainTransparent) score += 20;
  else score -= 10;

  // 月令旺相 +15
  if (monthStrength >= 3) score += 15;
  else if (monthStrength >= 2) score += 5;
  else score -= 10;

  // 清纯 +15
  if (isPure) score += 15;
  else score -= 10;

  // 护格 +10
  if (hasYinProtect) score += 10;
  if (hasCaiSupport) score += 8;

  // 有根 +10
  if (hasDayRoot) score += 10;

  // 身强身弱匹配度
  if (bodyStrength === 'strong' && ['正官格', '七杀格', '正财格', '偏财格', '食神格', '伤官格'].includes(gejuName)) {
    score += 5; // 身强用克泄耗，与格局方向一致
  } else if (bodyStrength === 'weak' && ['正印格', '偏印格', '比劫格'].includes(gejuName)) {
    score += 5; // 身弱用生扶，与格局方向一致
  }

  return Math.min(100, Math.max(10, Math.round(score)));
}

/**
 * 格局成色评语
 */
function buildPurityComment(purity: number): string {
  if (purity >= 85) return '格局成色上佳，四柱配置清纯有力，月令得时得气，格局成立条件充分，命盘层次较高。';
  if (purity >= 70) return '格局成色良好，四柱配置较为清纯，月令有气，格局基本成立，命盘层次中等偏上。';
  if (purity >= 55) return '格局成色中等，四柱配置有得有失，月令力弱或略有混杂，格局成立但需大运配合。';
  if (purity >= 40) return '格局成色一般，四柱配置不够理想，月令休囚或混杂较多，格局力弱，需借助大运流年补救。';
  return '格局成色偏低，四柱配置不利于格局成立，月令不得气且有混杂，命盘层次受限，需后天努力弥补。';
}

// ── 格局等级评定规则 ────────────────────────────────────────
function rateGejuLevel(
  monthBranch: Branch,
  dayStem: Stem,
  allStems: Stem[],
  mainShishen: Shishen,
): GejuLevel {
  const hidden = BRANCH_HIDDEN[monthBranch];
  const mainStem = hidden[0]?.stem;
  if (!mainStem) return '中格';

  const mainTransparent = allStems.includes(mainStem);
  const mainStemEl = STEM_ELEMENT[mainStem];
  const monthStrength = getMonthStrength(mainStemEl, monthBranch);
  const midStem = hidden[1]?.stem;
  const midTransparent = midStem ? allStems.includes(midStem) : false;

  if (mainTransparent && monthStrength >= 3) return '上格';
  if (mainTransparent && monthStrength >= 2) return '中上格';
  if (!mainTransparent && midTransparent && monthStrength >= 3) return '中上格';
  if (!mainTransparent && monthStrength >= 2) return '中格';
  return '中下格';
}

function getGejuLucky(gejuName: GejuName, dayStemEl: Element, fourPillars: FourPillars): Element[] {
  const { lucky } = deriveGejuLuckyUnlucky(gejuName, dayStemEl, fourPillars);
  return lucky;
}

function getGejuUnlucky(gejuName: GejuName, dayStemEl: Element, fourPillars: FourPillars): Element[] {
  const { unlucky } = deriveGejuLuckyUnlucky(gejuName, dayStemEl, fourPillars);
  return unlucky;
}

// ── 判断八字格局 ────────────────────────────────────────────
export function judgeGeju(fourPillars: FourPillars): GejuResult {
  const { year, month, day, hour } = fourPillars;
  const dayStem = day.stem;
  const dayStemEl = STEM_ELEMENT[dayStem];
  const monthBranch = month.branch;
  const allStems: Stem[] = [year.stem, month.stem, day.stem, hour.stem];
  const bodyStrength = calcDayStrength(fourPillars);

  // ── 月令主气十神 ─────────────────────────────────────────
  const monthHidden = BRANCH_HIDDEN[monthBranch];
  const mainMonthStem = monthHidden[0]?.stem;
  if (!mainMonthStem) {
    return buildDefaultGeju(fourPillars);
  }

  const mainShishen = getShishen(dayStem, mainMonthStem);

  // ── 检测从格（严格校验，成立度 < 60 返回 null）─────────
  const congGeju = checkCongGeju(fourPillars, mainShishen);
  if (congGeju) return congGeju;

  // ── 从格倾向探测（成立度 30-59，记录为备注而非判定）──────
  let congTendencyNote: string | undefined;
  {
    let congType: '从财格' | '从杀格' | '从儿格' | null = null;
    if (mainShishen === '正财' || mainShishen === '偏财') congType = '从财格';
    else if (mainShishen === '七杀' || mainShishen === '正官') congType = '从杀格';
    else if (mainShishen === '食神' || mainShishen === '伤官') congType = '从儿格';
    if (congType) {
      const { score } = calcCongEstablishment(fourPillars, congType);
      if (score >= 30 && score < 60) {
        congTendencyNote = `命局有${congType}倾向（从格探测得分 ${score}%），但尚不满足严格从格条件，此命按正格分析。`;
      }
    }
  }

  // ── 确定格局名 ───────────────────────────────────────────
  // 问真样例优先：阳干临羊刃月（甲卯、丙午、戊午、庚酉、壬子）直接取羊刃格；
  // 日干临月令禄位取建禄格，避免己午、庚申等被普通十神格局覆盖；
  // 辰戌丑未墓库月若印星透干，按杂气印格取名，避免只按月令主气落成比劫格。
  const specialGejuName = isYangRenMonth(dayStem, monthBranch)
    ? '羊刃格'
    : isJianLuMonth(dayStem, monthBranch)
      ? '建禄格'
    : findMixedQiYinPattern(dayStem, monthBranch, allStems);

  let gejuName: GejuName;
  if (specialGejuName) {
    gejuName = specialGejuName;
  } else {
    switch (mainShishen) {
      case '正官': gejuName = '正官格'; break;
      case '七杀': gejuName = '七杀格'; break;
      case '正印': gejuName = '正印格'; break;
      case '偏印': gejuName = '偏印格'; break;
      case '食神': gejuName = '食神格'; break;
      case '伤官': gejuName = '伤官格'; break;
      case '正财': gejuName = '正财格'; break;
      case '偏财': gejuName = '偏财格'; break;
      case '比肩':
      case '劫财': gejuName = '比劫格'; break;
      default: gejuName = '比劫格';
    }
  }

  // ── 格局名精化 ─────────────────────────────────────────
  if (!specialGejuName && monthHidden.length > 1) {
    const midStem = monthHidden[1]?.stem;
    if (midStem && allStems.includes(midStem)) {
      const midShishen = getShishen(dayStem, midStem);
      if ((mainShishen === '比肩' || mainShishen === '劫财') && midShishen !== '比肩' && midShishen !== '劫财') {
        switch (midShishen) {
          case '正官': gejuName = '正官格'; break;
          case '七杀': gejuName = '七杀格'; break;
          case '正印': gejuName = '正印格'; break;
          case '偏印': gejuName = '偏印格'; break;
          case '食神': gejuName = '食神格'; break;
          case '伤官': gejuName = '伤官格'; break;
          case '正财': gejuName = '正财格'; break;
          case '偏财': gejuName = '偏财格'; break;
          default: break;
        }
      }
    }
  }

  const level = rateGejuLevel(monthBranch, dayStem, allStems, mainShishen);
  const profile = GEJU_PROFILES[gejuName];
  const lucky = getGejuLucky(gejuName, dayStemEl, fourPillars);
  const unlucky = getGejuUnlucky(gejuName, dayStemEl, fourPillars);

  // ── 动态修正层计算 ───────────────────────────────────────
  const monthMainStem2 = monthHidden[0]?.stem ?? '';
  const monthMainTransparent = monthMainStem2 ? allStems.includes(monthMainStem2) : false;
  const monthMainStemEl = monthMainStem2 ? STEM_ELEMENT[monthMainStem2] : dayStemEl;
  const monthStrength = getMonthStrength(monthMainStemEl, monthBranch);

  const hasDayRoot = checkDayRoot(fourPillars);
  const hasYinProtect = checkYinProtect(fourPillars, gejuName);
  const hasCaiSupport = checkCaiSupport(fourPillars);
  const hasShiShangBreak = checkShiShangBreak(fourPillars, gejuName);
  const isPure = checkGejuPure(fourPillars, gejuName, mainShishen);

  const gejuPurity = calcGejuPurity(
    monthMainTransparent, monthStrength, isPure,
    hasYinProtect, hasCaiSupport, hasDayRoot,
    bodyStrength, gejuName,
  );

  const configAdjustments = buildConfigAdjustments(
    fourPillars, gejuName, mainShishen,
    monthMainTransparent, monthStrength, isPure,
    hasYinProtect, hasCaiSupport, hasShiShangBreak,
    hasDayRoot, bodyStrength,
  );

  const strengthDesc = STRENGTH_GEJU_DESC[gejuName][bodyStrength];
  const purityComment = buildPurityComment(gejuPurity);
  const tendencyText = buildTendencyText(gejuName, gejuPurity, false);

  // ── 构建描述 ─────────────────────────────────────────────
  const levelDesc = level === '上格' ? '格局清纯有力' : level === '中上格' ? '格局较为清纯' : '格局基本成立';
  const desc = `${levelDesc}，${profile.brief}。${profile.career}`;

  return {
    // 原有字段
    name: gejuName,
    level,
    mainShishen,
    desc,
    traits: profile.traits,
    lucky,
    unlucky,
    career: profile.career,
    love: profile.love,

    // 新增动态修正层
    dayStem,
    dayStemEl,
    bodyStrength,
    gejuPurity,
    configAdjustments,
    strengthDesc,
    purityComment,
    monthMainTransparent,
    monthStrength,
    isPure,
    hasYinProtect,
    hasCaiSupport,
    hasShiShangBreak,
    hasDayRoot,
    // v3
    establishment: gejuPurity,
    tendencyText,
    isCongGeju: false,
    congTendencyNote,
  };
}

function buildGejuDesc(
  gejuName: GejuName,
  level: GejuLevel,
  dayStem: Stem,
  monthBranch: Branch,
  allStems: Stem[],
): string {
  const profile = GEJU_PROFILES[gejuName];
  const levelDesc = level === '上格' ? '格局清纯有力' : level === '中上格' ? '格局较为清纯' : '格局基本成立';
  return `${levelDesc}，${profile.brief}。${profile.career}`;
}

function getLucky_Unlucky_Conflict(gejuName: GejuName, lucky: Element[]): Element[] {
  const allElements: Element[] = ['木', '火', '土', '金', '水'];
  const elementOvercomes: Record<Element, Element> = {
    木: '土', 土: '水', 水: '火', 火: '金', 金: '木',
  };
  const unlucky = new Set<Element>();
  for (const el of lucky) {
    unlucky.add(elementOvercomes[el]);
  }
  return [...unlucky].filter(e => !lucky.includes(e));
}

// ── 辅助：获取从格所从的五行 ────────────────────────────
function getCongTargetElement(congType: GejuName, dayStemEl: Element): Element | null {
  switch (congType) {
    case '从财格': return ELEMENT_OVERCOMES[dayStemEl];
    case '从杀格': return ELEMENT_OVERCOME_BY[dayStemEl];
    case '从儿格': return ELEMENT_GENERATES[dayStemEl];
    default: return null;
  }
}

/**
 * 从格严格多维成立度评分（0-100）
 * 必要条件：日主无根（否则返回极低分）
 * 充分条件：无印透干、无比劫透干、月令当旺、从势透干
 */
function calcCongEstablishment(
  fourPillars: FourPillars,
  congType: GejuName,
): { score: number; diagnosis: string } {
  const dayStem = fourPillars.day.stem;
  const dayStemEl = STEM_ELEMENT[dayStem];
  const allBranches = [
    fourPillars.year.branch, fourPillars.month.branch,
    fourPillars.day.branch, fourPillars.hour.branch,
  ];
  const allStems = [
    fourPillars.year.stem, fourPillars.month.stem,
    fourPillars.day.stem, fourPillars.hour.stem,
  ];
  const dims: string[] = [];

  // ① 必要条件：日主在地支完全无根（含余气）
  let hasAnyRoot = false;
  for (const b of allBranches) {
    for (const h of BRANCH_HIDDEN[b]) {
      if (STEM_ELEMENT[h.stem] === dayStemEl) {
        hasAnyRoot = true;
        break;
      }
    }
    if (hasAnyRoot) break;
  }
  if (hasAnyRoot) {
    return { score: 5, diagnosis: '日主有根（从格必要条件不满足，否决）' };
  }
  dims.push('日主无根✓');
  let score = 40;

  // ② 天干无印星（印能救身，有印严重扣分）
  const yinEl = ELEMENT_GENERATED_BY[dayStemEl];
  const hasYinStem = allStems.some(s => STEM_ELEMENT[s] === yinEl);
  if (!hasYinStem) {
    score += 20;
    dims.push('无印透干✓');
  } else {
    score -= 25;
    dims.push('印星透干✗(-25)');
  }

  // ③ 天干无比劫（比劫帮身，有则减弱从势）
  const hasBijStem = allStems.filter(s => s !== dayStem).some(s => STEM_ELEMENT[s] === dayStemEl);
  if (!hasBijStem) {
    score += 15;
    dims.push('无比劫透干✓');
  } else {
    score -= 12;
    dims.push('比劫透干✗(-12)');
  }

  // ④ 月令旺相（得时者从势更稳）
  const monthBranch = fourPillars.month.branch;
  const monthHidden = BRANCH_HIDDEN[monthBranch];
  const monthMainStem = monthHidden[0]?.stem;
  if (monthMainStem) {
    const mEl = STEM_ELEMENT[monthMainStem];
    const mStr = getMonthStrength(mEl, monthBranch);
    if (mStr >= 3) { score += 15; dims.push('月令当旺✓(+15)'); }
    else if (mStr >= 2) { score += 8; dims.push('月令相气+8'); }
    else { score -= 5; dims.push('月令休囚✗(-5)'); }
  }

  // ⑤ 从的五行透出天干（从势有形）
  const congEl = getCongTargetElement(congType, dayStemEl);
  const hasCongStem = congEl ? allStems.some(s => STEM_ELEMENT[s] === congEl) : false;
  if (hasCongStem) { score += 10; dims.push('从势透干✓(+10)'); }
  else { dims.push('从势未透干'); }

  const finalScore = Math.min(100, Math.max(0, score));
  return {
    score: finalScore,
    diagnosis: dims.join(' · '),
  };
}

/**
 * 格局倾向文本生成
 * 不直接说"你是XX格"，而是"更接近XX格思路，成立度 XX%"
 */
function buildTendencyText(name: GejuName, establishment: number, isCongGeju: boolean): string {
  if (isCongGeju) {
    if (establishment >= 80) return `命局符合${name}格局特征，成立度 ${establishment}%`;
    if (establishment >= 65) return `更接近${name}思路，成立度 ${establishment}%`;
    return `有${name}倾向，成立度 ${establishment}%`;
  }
  // 正格
  if (establishment >= 82) return `更接近${name}思路，格局清晰，成立度 ${establishment}%`;
  if (establishment >= 65) return `更接近${name}思路，成立度 ${establishment}%`;
  if (establishment >= 48) return `格局倾向${name}，成立度 ${establishment}%，有待大运配合`;
  return `有${name}格局倾向，成立度较低（${establishment}%），格局待成`;
}

// ── 正统从格判断（严格多维校验版）──────────────────────────
function checkCongGeju(fourPillars: FourPillars, mainShishen: Shishen): GejuResult | null {
  const { year, month, day, hour } = fourPillars;
  const dayStem = day.stem;
  const dayStemEl = STEM_ELEMENT[dayStem];
  const allBranches = [year.branch, month.branch, day.branch, hour.branch];
  const allStems = [year.stem, month.stem, day.stem, hour.stem];

  // 快速否决：月令主气是比劫或印绶，直接不从
  if (mainShishen === '比肩' || mainShishen === '劫财') return null;
  if (mainShishen === '正印' || mainShishen === '偏印') return null;

  // 确认从格类型
  let congType: '从财格' | '从杀格' | '从儿格' | null = null;
  if (mainShishen === '正财' || mainShishen === '偏财') congType = '从财格';
  else if (mainShishen === '七杀' || mainShishen === '正官') congType = '从杀格';
  else if (mainShishen === '食神' || mainShishen === '伤官') congType = '从儿格';
  if (!congType) return null;

  // 严格多维评分
  const { score: congScore, diagnosis } = calcCongEstablishment(fourPillars, congType);

  // 阈值：成立度 >= 60 才判从格
  if (congScore < 60) return null;

  return buildCongResult(congType, fourPillars, congScore, diagnosis);
}

function buildCongResult(name: GejuName, fourPillars: FourPillars, congScore: number, diagnosis: string): GejuResult {
  const profile = GEJU_PROFILES[name];
  const dayStem = fourPillars.day.stem;
  const dayStemEl = STEM_ELEMENT[dayStem];
  const bodyStrength = calcDayStrength(fourPillars);
  const { lucky, unlucky } = deriveGejuLuckyUnlucky(name, dayStemEl, fourPillars);

  const allStems = [fourPillars.year.stem, fourPillars.month.stem, fourPillars.day.stem, fourPillars.hour.stem];

  const monthHidden = BRANCH_HIDDEN[fourPillars.month.branch];
  const monthMainStem2 = monthHidden[0]?.stem ?? '';
  const monthMainTransparent = monthMainStem2 ? allStems.includes(monthMainStem2) : false;
  const monthMainStemEl = monthMainStem2 ? STEM_ELEMENT[monthMainStem2] : dayStemEl;
  const monthStrength = getMonthStrength(monthMainStemEl, fourPillars.month.branch);

  const hasDayRoot = false;
  const hasYinProtect = checkYinProtect(fourPillars, name);
  const hasCaiSupport = checkCaiSupport(fourPillars);
  const hasShiShangBreak = checkShiShangBreak(fourPillars, name);
  const isPure = true;
  const mainShishen = getShishen(dayStem, monthMainStem2 || dayStem);

  const gejuPurity = calcGejuPurity(
    monthMainTransparent, monthStrength, isPure,
    hasYinProtect, hasCaiSupport, hasDayRoot,
    bodyStrength, name,
  );

  const configAdjustments = buildConfigAdjustments(
    fourPillars, name, mainShishen,
    monthMainTransparent, monthStrength, isPure,
    hasYinProtect, hasCaiSupport, hasShiShangBreak,
    hasDayRoot, bodyStrength,
  );

  const strengthDesc = STRENGTH_GEJU_DESC[name][bodyStrength];
  const purityComment = buildPurityComment(gejuPurity);
  const tendencyText = buildTendencyText(name, congScore, true);

  return {
    name,
    level: '中上格',
    mainShishen,
    desc: `${profile.brief}。${profile.career}`,
    traits: profile.traits,
    lucky,
    unlucky,
    career: profile.career,
    love: profile.love,
    dayStem,
    dayStemEl,
    bodyStrength,
    gejuPurity,
    configAdjustments,
    strengthDesc,
    purityComment,
    monthMainTransparent,
    monthStrength,
    isPure,
    hasYinProtect,
    hasCaiSupport,
    hasShiShangBreak,
    hasDayRoot,
    // v3
    establishment: congScore,
    tendencyText,
    isCongGeju: true,
    congDiagnosis: diagnosis,
  };
}

function buildDefaultGeju(fourPillars: FourPillars): GejuResult {
  const profile = GEJU_PROFILES['比劫格'];
  const dayStem = fourPillars.day.stem;
  const dayStemEl = STEM_ELEMENT[dayStem];
  const bodyStrength = calcDayStrength(fourPillars);
  const { lucky, unlucky } = deriveGejuLuckyUnlucky('比劫格', dayStemEl, fourPillars);

  const allStems = [fourPillars.year.stem, fourPillars.month.stem, fourPillars.day.stem, fourPillars.hour.stem];
  const monthHidden = BRANCH_HIDDEN[fourPillars.month.branch];
  const monthMainStem2 = monthHidden[0]?.stem ?? '';
  const monthMainTransparent = monthMainStem2 ? allStems.includes(monthMainStem2) : false;
  const monthMainStemEl = monthMainStem2 ? STEM_ELEMENT[monthMainStem2] : dayStemEl;
  const monthStrength = getMonthStrength(monthMainStemEl, fourPillars.month.branch);

  const hasDayRoot = checkDayRoot(fourPillars);
  const hasYinProtect = checkYinProtect(fourPillars, '比劫格');
  const hasCaiSupport = checkCaiSupport(fourPillars);
  const hasShiShangBreak = checkShiShangBreak(fourPillars, '比劫格');
  const isPure = checkGejuPure(fourPillars, '比劫格', '比肩');

  const gejuPurity = calcGejuPurity(
    monthMainTransparent, monthStrength, isPure,
    hasYinProtect, hasCaiSupport, hasDayRoot,
    bodyStrength, '比劫格',
  );

  const configAdjustments = buildConfigAdjustments(
    fourPillars, '比劫格', '比肩',
    monthMainTransparent, monthStrength, isPure,
    hasYinProtect, hasCaiSupport, hasShiShangBreak,
    hasDayRoot, bodyStrength,
  );

  const strengthDesc = STRENGTH_GEJU_DESC['比劫格'][bodyStrength];
  const purityComment = buildPurityComment(gejuPurity);
  const tendencyText = buildTendencyText('比劫格', gejuPurity, false);

  return {
    name: '比劫格',
    level: '中格',
    mainShishen: '比肩',
    desc: profile.brief,
    traits: profile.traits,
    lucky,
    unlucky,
    career: profile.career,
    love: profile.love,
    dayStem,
    dayStemEl,
    bodyStrength,
    gejuPurity,
    configAdjustments,
    strengthDesc,
    purityComment,
    monthMainTransparent,
    monthStrength,
    isPure,
    hasYinProtect,
    hasCaiSupport,
    hasShiShangBreak,
    hasDayRoot,
    // v3
    establishment: gejuPurity,
    tendencyText,
    isCongGeju: false,
  };
}

// ELEMENT_COLORS already imported and re-exported from shishenEngine.ts

/**
 * 问真口径「格局名」（用于「基本资讯·格局参考」对齐问真，2026-09-05）。
 * 独立于 judgeGeju：不改动主引擎（洞察/AI/App 仍用 judgeGeju），仅提供展示层可用的问真命名。
 *
 * 现状：四库月（辰戌丑未）用问真杂气取格法，其余月暂时回退 judgeGeju。
 * 杂气取法（oracle 131 条命中 123/93.9%）：
 *   天干（年/月/时，不含日）中凡与月支任一藏干相同者 → 取其对该日主十神 → 杂气X格；
 *   无一藏干透出 → 取月支本气十神 → 杂气X格。
 *   注：比劫不计入杂气（问真无「杂气比劫格」），命中比劫时回退 judgeGeju。
 * 待补：羊刃非常规触发（阴干劫财当令）、从格阈值、专旺（润下/稼穑/炎上/从革/曲直）等后续 oracle 拟合。
 */
const WENZHEN_ZAQI_TEN_GODS = new Set<Shishen>([
  '正官', '七杀', '正印', '偏印', '食神', '伤官', '正财', '偏财',
]);

export function wenzhenGejuName(fourPillars: FourPillars): string {
  const month = fourPillars.month;
  const monthBranch = month.branch;
  const fallback = judgeGeju(fourPillars).name;
  if (!STORAGE_BRANCHES.has(monthBranch)) {
    return fallback;
  }
  const dayStem = fourPillars.day.stem;
  const hiddenStems = (BRANCH_HIDDEN[monthBranch] ?? []).map((h) => h.stem);
  if (hiddenStems.length === 0) {
    return fallback;
  }
  // 年/月/时天干（不含日主）按顺序取第一个「透出藏干」
  const outerStems = [fourPillars.year.stem, month.stem, fourPillars.hour.stem];
  const visible = outerStems.find((s) => hiddenStems.includes(s));
  const picked = visible ?? hiddenStems[0];
  const shishenName = getShishen(dayStem, picked);
  if (!WENZHEN_ZAQI_TEN_GODS.has(shishenName)) {
    return fallback;
  }
  return `杂气${shishenName}格`;
}
